import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Videocam as VideocamIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  CloudQueue as CloudIcon,
  Public as PublicIcon,
  Speed as SpeedIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const RVSSComponentDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ift-rvss-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to IFT/RVSS System"
        >
          Back to IFT/RVSS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Remote Video Surveillance System (RVSS)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Mobile and Fixed Camera Network for Border Monitoring
          </Typography>
        </Box>
      </Box>

      {/* Component Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Component Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Remote Video Surveillance System (RVSS) is a flexible network of mobile and fixed camera installations 
            that provides real-time video monitoring capabilities along the U.S. borders. Unlike the permanent IFT towers, 
            RVSS units can be rapidly deployed to respond to changing threat patterns, special operations, or temporary 
            surveillance needs. Operators at sector operations centers remotely control PTZ (Pan-Tilt-Zoom) cameras, 
            enabling dynamic coverage of high-activity areas, tactical operations support, and evidence collection for 
            prosecutions.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Component Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="RVSS Component 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is RVSS"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>network of remotely operated video surveillance cameras</strong> including both <strong>mobile 
                    deployable units and fixed installations</strong>. Features <strong>PTZ (Pan-Tilt-Zoom) cameras with 
                    day/night capability, infrared illumination, and weather-resistant enclosures</strong>. Provides 
                    <strong> real-time video streaming, motion-triggered recording, and remote operator control</strong> from 
                    sector operations centers.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses RVSS"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>U.S. Border Patrol surveillance operators</strong> at sector operations centers. 
                    Primary users include <strong>remote video operators, tactical coordinators, field agents, sector 
                    intelligence units, and investigative teams</strong>. Maintained by <strong>CBP technical support 
                    contractors</strong> and <strong>Border Patrol technology specialists</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was RVSS deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initial deployment in <strong>2005-2008</strong> as part of Secure Border Initiative. Expanded 
                    significantly in <strong>2010-2015</strong> with mobile unit proliferation. Technology upgrades in 
                    <strong> 2018-2020</strong> with HD cameras and enhanced night vision. <strong>2021-present</strong> 
                    integration with AI analytics. Operates <strong>24/7</strong> with <strong>on-demand deployment</strong> 
                    for tactical operations.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is RVSS deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed along <strong>U.S.-Mexico and U.S.-Canada borders</strong> with concentration in <strong>high-traffic 
                    smuggling corridors, urban border areas, and tactical operation zones</strong>. Mobile units can be 
                    <strong> rapidly repositioned</strong> based on intelligence. Fixed installations at <strong>ports of entry, 
                    known crossing points, and infrastructure protection sites</strong>. Monitored from <strong>9 Border Patrol 
                    sector operations centers</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why RVSS exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>flexible, rapidly deployable surveillance capability</strong> that complements permanent 
                    infrastructure. Enables <strong>tactical response to changing threat patterns, special operations support, 
                    and temporary coverage gaps</strong>. Critical for <strong>evidence collection, real-time situational 
                    awareness, and agent safety</strong>. Lower cost alternative to permanent towers for <strong>dynamic 
                    surveillance needs</strong> and <strong>short-term operations</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* RVSS Components */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <VideocamIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        RVSS System Components
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VideocamIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Mobile Camera Units
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Trailer-mounted deployable surveillance systems for tactical operations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Rapid Deployment:</strong> Setup in 30 minutes or less</Typography></li>
                <li><Typography variant="body2"><strong>Trailer Mounted:</strong> Towable by standard vehicles</Typography></li>
                <li><Typography variant="body2"><strong>Telescoping Mast:</strong> 30-50 foot camera elevation</Typography></li>
                <li><Typography variant="body2"><strong>Self-Contained Power:</strong> Solar panels and battery systems</Typography></li>
                <li><Typography variant="body2"><strong>Wireless Connectivity:</strong> Cellular and satellite links</Typography></li>
                <li><Typography variant="body2"><strong>Ruggedized Design:</strong> All-terrain, all-weather capability</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Fixed Camera Installations
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Permanent camera positions at strategic locations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Strategic Placement:</strong> High-traffic crossing points and chokepoints</Typography></li>
                <li><Typography variant="body2"><strong>Permanent Mounting:</strong> Poles, buildings, and infrastructure</Typography></li>
                <li><Typography variant="body2"><strong>Grid Power:</strong> Reliable commercial power with UPS backup</Typography></li>
                <li><Typography variant="body2"><strong>Hardened Enclosures:</strong> Vandal-resistant and weatherproof</Typography></li>
                <li><Typography variant="body2"><strong>Fiber Connectivity:</strong> High-bandwidth dedicated connections</Typography></li>
                <li><Typography variant="body2"><strong>24/7 Operation:</strong> Continuous monitoring capability</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VisibilityIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Camera Technology
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced imaging systems for day and night surveillance.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>HD Resolution:</strong> 1080p or higher video quality</Typography></li>
                <li><Typography variant="body2"><strong>PTZ Control:</strong> 360° pan, 90° tilt, 30x optical zoom</Typography></li>
                <li><Typography variant="body2"><strong>Low-Light Cameras:</strong> Enhanced sensitivity for night operation</Typography></li>
                <li><Typography variant="body2"><strong>IR Illumination:</strong> Infrared lighting for complete darkness</Typography></li>
                <li><Typography variant="body2"><strong>Image Stabilization:</strong> Compensates for wind and vibration</Typography></li>
                <li><Typography variant="body2"><strong>Auto-Focus:</strong> Maintains sharp images at all zoom levels</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Recording & Storage
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Video recording and evidence management systems.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>24/7 Recording:</strong> Continuous video capture and archiving</Typography></li>
                <li><Typography variant="body2"><strong>Motion Triggers:</strong> Event-based recording activation</Typography></li>
                <li><Typography variant="body2"><strong>Local Storage:</strong> On-unit DVR/NVR systems</Typography></li>
                <li><Typography variant="body2"><strong>Cloud Backup:</strong> Redundant storage at operations centers</Typography></li>
                <li><Typography variant="body2"><strong>Retention Policy:</strong> 30-90 day video retention</Typography></li>
                <li><Typography variant="body2"><strong>Evidence Export:</strong> Chain of custody for prosecutions</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Deployment Types */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Deployment Scenarios
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Scenario</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unit Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Duration</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Use Case</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Tactical Operations</strong></TableCell>
              <TableCell>Mobile Units</TableCell>
              <TableCell>Days to Weeks</TableCell>
              <TableCell>Special operations, surge deployments</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Permanent Monitoring</strong></TableCell>
              <TableCell>Fixed Installations</TableCell>
              <TableCell>Years</TableCell>
              <TableCell>High-traffic areas, ports of entry</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Intelligence Gathering</strong></TableCell>
              <TableCell>Mobile Units</TableCell>
              <TableCell>Weeks to Months</TableCell>
              <TableCell>Pattern analysis, smuggling routes</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Event Security</strong></TableCell>
              <TableCell>Mobile Units</TableCell>
              <TableCell>Hours to Days</TableCell>
              <TableCell>Special events, VIP protection</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Gap Coverage</strong></TableCell>
              <TableCell>Mobile Units</TableCell>
              <TableCell>Weeks to Months</TableCell>
              <TableCell>Temporary coverage during construction</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Infrastructure Protection</strong></TableCell>
              <TableCell>Fixed Installations</TableCell>
              <TableCell>Years</TableCell>
              <TableCell>Critical infrastructure, facilities</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                200+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mobile Units
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Deployable cameras
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                500+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fixed Cameras
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Permanent installations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Continuous monitoring
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                &lt;30min
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Deployment Time
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Mobile unit setup
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Operational Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Advantages
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Flexibility:</strong> Rapid redeployment to changing threats</Typography></li>
                <li><Typography variant="body2"><strong>Cost Effective:</strong> Lower cost than permanent infrastructure</Typography></li>
                <li><Typography variant="body2"><strong>Tactical Support:</strong> Real-time intelligence for operations</Typography></li>
                <li><Typography variant="body2"><strong>Evidence Collection:</strong> Video documentation for prosecutions</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Remote Operation:</strong> Operator safety from secure locations</Typography></li>
                <li><Typography variant="body2"><strong>Scalability:</strong> Easy to add units as needed</Typography></li>
                <li><Typography variant="body2"><strong>Coverage Gaps:</strong> Fill temporary surveillance needs</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Use:</strong> Supports various mission types</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Points */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Integrations
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="Operations Centers" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Remote monitoring stations</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="IFT Network" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Fixed tower integration</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Agent Tablets" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Field access to video</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Recording Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">DVR/NVR infrastructure</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="GIS Mapping" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Geographic positioning</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Alert Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Automated notifications</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Evidence Management" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Chain of custody</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Communications" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Radio and data networks</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default RVSSComponentDetail;
